import { PhoneCall } from "lucide-react";
import { ActionMenu } from "@/components/ActionMenu";
import { useToast } from "@/components/Toast";
import { formatPhone } from "@/lib/format";
import { cn } from "@/lib/utils";
import { recordDial, VOICE_WINDOW, voiceDialUrl, type DialContext } from "@/lib/voice";

/**
 * The one dial control, everywhere (Leads and Curative alike).
 *
 * v2.10 P1.2 (D-133): the NUMBER is the Google Voice link — it opens Voice's own "Call …? Cancel / Call"
 * dialog (D-128) in one reused `cornerstone-voice` window, in hello@'s account, copies the number, and
 * records the press (`dial_clicked`, which the dial session waits on). The ⋯ beside it holds "Call with
 * phone — shows your cell number" (`tel:`, the carrier line: the 201 caller ID that Voice never sees) and
 * "Copy number". No `tel:` is reachable with a single tap.
 *
 * Nothing here places a call (CLAUDE.md rule 1): Sam presses Call inside Voice.
 */
export function PhoneLink({
  e164,
  testId,
  compact = false,
  leadId,
  dealId,
  surface,
}: {
  e164: string | null | undefined;
  testId?: string;
  /** In a dense row (the Leads table, a board card): smaller text. The controls are the same. */
  compact?: boolean;
} & DialContext) {
  const toast = useToast();
  if (!e164) return <span className="text-fg-muted">—</span>;
  const copy = () => {
    void navigator.clipboard?.writeText(e164).catch(() => undefined);
  };
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={testId}>
      <a
        href={voiceDialUrl(e164)}
        target={VOICE_WINDOW}
        title="Call from Google Voice (your 931 number) — you press Call in Voice"
        aria-label={`Call ${formatPhone(e164)} from Google Voice`}
        data-testid={compact ? "phone-voice-button" : testId ? `${testId}-voice` : "phone-voice"}
        data-dial="voice"
        className={cn(
          "inline-flex items-center gap-1 rounded tabular-nums text-accent hover:underline",
          "pointer-coarse:min-h-11 pointer-coarse:px-1",
          compact ? "text-[11px]" : "text-sm",
        )}
        onClick={(e) => {
          e.stopPropagation();
          copy();
          recordDial(e164, { leadId, dealId, surface });
        }}
      >
        <PhoneCall className={compact ? "size-3" : "size-3.5"} aria-hidden />
        <span>{formatPhone(e164)}</span>
        <span className="sr-only"> via Voice</span>
      </a>
      <ActionMenu
        testId={testId ? `${testId}-more` : "phone-more"}
        items={[
          {
            label: "Call with phone — shows your cell number",
            href: `tel:${e164}`,
            testId: testId ? `${testId}-tel` : "phone-tel",
            onSelect: () => undefined,
          },
          {
            label: "Copy number",
            testId: "phone-copy",
            onSelect: () => {
              copy();
              toast.push("Number copied");
            },
          },
        ]}
      />
    </span>
  );
}
